/* ============================================================
   WESTSIDE BARBELL: SEARCH PAGE TABS
   Products / Articles / Pages for sections/wsbb-search.liquid.

   - Products: the filter engine (wsbb-filter.js) loads and counts itself,
     and reports its total in a wsbb:pf-ready event.
   - Articles (the blog's article cards) and Pages: each tab loads page 1
     of its own view up front,
     so every tab shows a count immediately; "Load more" appends the
     next page.
   - The tabs and the Products panel render from the first paint with
     placeholders (see sections/wsbb-search), so this fills in the page
     rather than replacing it.
   - Articles "Sort by": Relevance (the search's order), Newest, Recently
     updated, Oldest first, as on the blog. Every result's keys come from ?view=articlekeys
     and are sorted here; cards come one by one from the blog's ?view=card,
     24 at a time as "Load more" asks. ?article_sort= keeps the choice.
   - Opens on ?tab= if present (the old theme's param); an inline script
     has already shown that tab before first paint. Otherwise it opens
     the first tab that has results: Products, as the old theme always did,
     unless the query matched no products. Then it opens Articles or Pages
     rather than an empty tab.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-wsbb-sr]');
  var tablist = root && root.querySelector('[data-search-tabs]');
  if (!tablist || !window.fetch) return;

  var ORDER = ['products', 'articles', 'pages'];
  var totals = {};
  var tabs = {};
  var panels = {};
  ORDER.forEach(function (name) {
    tabs[name] = tablist.querySelector('[data-tab="' + name + '"]');
    panels[name] = root.querySelector('[data-panel="' + name + '"]');
  });

  // ?tab=, or Shopify's own ?type=article / ?type=page (older links and forms).
  var params = new URLSearchParams(location.search);
  var requested = params.get('tab') || { article: 'articles', page: 'pages' }[params.get('type')] || null;
  var active = null;

  function parse(html) {
    var box = document.createElement('div');
    box.innerHTML = html;
    return box.querySelector('[data-search-view]');
  }

  function setCount(name, n) {
    totals[name] = n;
    tabs[name].querySelector('[data-tab-count]').textContent = n == null ? '' : '(' + n + ')';
    var none = panels[name].querySelector('[data-panel-none]');
    if (none) none.hidden = n !== 0;
    maybeStart();
  }

  /* ---------- Articles & Pages ---------- */

  function loadView(name, page) {
    var panel = panels[name];
    var url = new URL(panel.getAttribute('data-view-url'), location.origin);
    url.searchParams.set('page', page);
    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var view = parse(html);
        if (!view) throw new Error('no view');
        var list = panel.querySelector('[data-panel-list]');
        list.querySelectorAll('.wsbb-sr-skel').forEach(function (el) { el.remove(); });
        while (view.firstElementChild) list.appendChild(view.firstElementChild);
        // Article cards carry tags; let wsbb-blog.js tidy their separators.
        document.dispatchEvent(new CustomEvent('wsbb:cards-updated'));
        var pages = Number(view.getAttribute('data-pages')) || 1;
        var more = panel.querySelector('[data-panel-more]');
        more.hidden = page >= pages;
        more.setAttribute('data-next', page + 1);
        if (page === 1) setCount(name, Number(view.getAttribute('data-total')) || 0);
      })
      .catch(function () { if (page === 1) setCount(name, null); });
  }

  var firstLoad = {};
  var sortedMore = null;   // set while Articles are sorted: loads the next batch

  ['articles', 'pages'].forEach(function (name) {
    var more = panels[name].querySelector('[data-panel-more]');
    more.addEventListener('click', function () {
      var btn = more.querySelector('button');
      var label = btn.textContent;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Loading\u2026';
      var next = name === 'articles' && sortedMore ? sortedMore() : loadView(name, Number(more.getAttribute('data-next')) || 2);
      next.then(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
      });
    });
    firstLoad[name] = loadView(name, 1);
  });

  /* ---------- Articles: Sort by ---------- */

  (function () {
    var panel = panels.articles;
    var sel = panel.querySelector('[data-sr-article-sort]');
    var bar = panel.querySelector('[data-sr-sortbar]');
    if (!sel || !bar) return;
    var list = panel.querySelector('[data-panel-list]');
    var more = panel.querySelector('[data-panel-more]');
    var keysUrl = sel.getAttribute('data-keys-url');
    var PER = 24;
    var keysPromise = null;
    var order = [];
    var shown = 0;
    var current = '';
    var relevance = null;  // the relevance cards and Load more state, to go back to

    function loadKeys() {
      if (keysPromise) return keysPromise;
      var page = function (n) {
        return fetch(keysUrl + '&page=' + n, { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); });
      };
      keysPromise = page(1).then(function (first) {
        var rest = [];
        for (var n = 2; n <= (first.pages || 1); n++) rest.push(page(n));
        return Promise.all(rest).then(function (others) {
          return [].concat.apply([], [first].concat(others).map(function (p) { return p.items || []; })).filter(Boolean);
        });
      });
      keysPromise.catch(function () { keysPromise = null; });  // allow a retry
      return keysPromise;
    }
    // Warm the keys when the reader shows interest in the dropdown.
    ['pointerenter', 'focus', 'touchstart'].forEach(function (ev) {
      sel.addEventListener(ev, function () { loadKeys().catch(function () {}); }, { once: true, passive: true });
    });

    // One card from the blog's own template (templates/article.card).
    function fetchCard(handle) {
      return fetch('/blogs/' + handle + '?view=card', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var t = document.createElement('template');
          t.innerHTML = html.trim();
          var card = t.content.querySelector('.wsbb-card');
          if (!card) throw new Error('no card for ' + handle);
          return card;
        });
    }

    function busy(on) { list.classList.toggle('is-sorting', on); list.setAttribute('aria-busy', on ? 'true' : 'false'); }
    function cardsUpdated() { document.dispatchEvent(new CustomEvent('wsbb:cards-updated')); }

    function reflect() {
      if (window.Shopify && window.Shopify.designMode) return;
      var url = new URL(location.href);
      if (current) url.searchParams.set('article_sort', current); else url.searchParams.delete('article_sort');
      var next = url.pathname + url.search + url.hash;
      if (next !== location.pathname + location.search + location.hash) history.replaceState(history.state, '', next);
    }

    // The next batch of the sorted order, appended (Load more).
    function nextBatch() {
      var batch = order.slice(shown, shown + PER);
      return Promise.all(batch.map(fetchCard)).then(function (cards) {
        cards.forEach(function (c) { list.appendChild(c); });
        shown += batch.length;
        more.hidden = shown >= order.length;
        cardsUpdated();
      });
    }

    function sortBy(value) {
      if (value === current) return Promise.resolve();
      if (!current) {
        relevance = { cards: Array.prototype.slice.call(list.children), next: more.getAttribute('data-next'), hidden: more.hidden };
      }
      if (!value) {
        // Back to relevance: the cards as they were, no refetch.
        list.innerHTML = '';
        relevance.cards.forEach(function (c) { list.appendChild(c); });
        more.setAttribute('data-next', relevance.next);
        more.hidden = relevance.hidden;
        current = '';
        sortedMore = null;
        reflect();
        cardsUpdated();
        return Promise.resolve();
      }
      busy(true);
      return loadKeys().then(function (items) {
        // Newest key first; the search's own order breaks ties.
        // Newest / Recently updated: latest key first. Oldest first: earliest
        // publish date first. The search's own order breaks ties.
        var col = value === 'updated' ? 2 : 1;
        var dir = value === 'oldest' ? -1 : 1;
        var ranked = items.map(function (it, i) { return { h: it[0], k: it[col] || '', i: i }; });
        ranked.sort(function (a, b) { return a.k !== b.k ? (a.k < b.k ? dir : -dir) : a.i - b.i; });
        var want = ranked.map(function (o) { return o.h; });
        // Reuse cards already on the page; fetch only the rest.
        var have = {};
        (relevance ? relevance.cards : []).concat(Array.prototype.slice.call(list.children)).forEach(function (c) {
          if (c.getAttribute && c.getAttribute('data-handle')) have[c.getAttribute('data-handle')] = c;
        });
        var first = want.slice(0, PER);
        return Promise.all(first.map(function (h) { return have[h] ? have[h] : fetchCard(h); })).then(function (cards) {
          list.innerHTML = '';
          cards.forEach(function (c) { list.appendChild(c); });
          order = want;
          shown = first.length;
          more.hidden = shown >= order.length;
          current = value;
          sortedMore = nextBatch;
          reflect();
          cardsUpdated();
        });
      }).catch(function () {
        sel.value = current;   // couldn't sort: stay as we were
      }).then(function () { busy(false); });
    }

    sel.addEventListener('change', function () { sortBy(sel.value); });

    // Once the first page is in: show the control if there's anything to
    // sort, and apply ?article_sort= from the address.
    firstLoad.articles.then(function () {
      var n = totals.articles || 0;
      panel.querySelector('[data-sr-article-count]').textContent = n + ' ' + (n === 1 ? 'article' : 'articles');
      bar.hidden = !(n > 1);
      var wanted = new URLSearchParams(location.search).get('article_sort');
      if (!bar.hidden && /^(newest|updated|oldest)$/.test(wanted || '')) {
        sel.value = wanted;
        sortBy(wanted);
      }
    });
  })();

  /* ---------- Products ---------- */

  document.addEventListener('wsbb:pf-ready', function (e) {
    setCount('products', e.detail ? e.detail.total : null);
  });
  // The engine may already have finished before this script ran.
  if ('wsbbPfTotal' in window) setCount('products', window.wsbbPfTotal);

  /* ---------- Tabs ---------- */

  function select(name, focus) {
    active = name;
    root.removeAttribute('data-initial-tab');   // the pre-paint choice is now real
    ORDER.forEach(function (n) {
      var on = n === name;
      tabs[n].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[n].tabIndex = on ? 0 : -1;
      panels[n].hidden = !on;
    });
    if (focus) tabs[name].focus();

    // Only when it changes, and never in the theme editor (see wsbb-filter.js setURL).
    var url = new URL(location.href);
    if (name === 'products') url.searchParams.delete('tab'); else url.searchParams.set('tab', name);
    var next = url.pathname + url.search + url.hash;
    if (!(window.Shopify && window.Shopify.designMode) && next !== location.pathname + location.search + location.hash) {
      history.replaceState(history.state, '', next);
    }
  }

  // Open once every count is in (or has failed), so the choice of first tab
  // is made on real numbers rather than whichever request finished first.
  function maybeStart() {
    if (active) return;
    if (!ORDER.every(function (n) { return n in totals; })) return;

    var pick = ORDER.indexOf(requested) > -1 ? requested : null;
    if (!pick) pick = ORDER.filter(function (n) { return totals[n] > 0; })[0] || 'products';

    var fallback = root.querySelector('[data-search-fallback]');
    if (fallback) fallback.hidden = true;
    root.classList.add('is-ready');
    var status = root.querySelector('[data-sr-status]');
    if (status) {
      status.textContent = ORDER.map(function (n) {
        return (totals[n] == null ? 'unknown' : totals[n]) + ' ' + n;
      }).join(', ') + '.';
    }
    tablist.hidden = false;
    select(pick, false);

    // Every count failed: nothing to tab between, so show Shopify's own list.
    if (ORDER.every(function (n) { return totals[n] == null; })) {
      tablist.hidden = true;
      panels[pick].hidden = true;
      if (fallback) { fallback.hidden = false; fallback.classList.add('is-shown'); }
    }
  }

  tablist.addEventListener('click', function (e) {
    var t = e.target.closest('[data-tab]');
    if (t) select(t.getAttribute('data-tab'), false);
  });

  tablist.addEventListener('keydown', function (e) {
    var i = ORDER.indexOf(active);
    var next = null;
    if (e.key === 'ArrowRight') next = ORDER[(i + 1) % ORDER.length];
    if (e.key === 'ArrowLeft') next = ORDER[(i + ORDER.length - 1) % ORDER.length];
    if (e.key === 'Home') next = ORDER[0];
    if (e.key === 'End') next = ORDER[ORDER.length - 1];
    if (next) { e.preventDefault(); select(next, true); }
  });

  // If the filter engine never reports (script blocked, say), don't leave the
  // page stuck on the fallback forever.
  setTimeout(function () { if (!('products' in totals)) setCount('products', null); }, 15000);
})();
